import { createScopedErrors, reportInvalidInput as reportInvalidInput$1 } from '@norkron/web-errors';
import { useRef, useMemo, useLayoutEffect, useCallback, useState } from 'react';
import { jsxs, jsx } from 'react/jsx-runtime';

// src/markdown-syntax.ts
var PLACEHOLDER = {
  bold: "bold",
  italic: "italic",
  strikethrough: "strikethrough",
  inlineCode: "code"
};
var MARKER = {
  bold: "**",
  italic: "*",
  strikethrough: "~~",
  inlineCode: "`"
};
function applyMarkdownAction(state, action) {
  switch (action.type) {
    case "bold":
    case "italic":
    case "strikethrough":
    case "inlineCode":
      return wrapInline(state, MARKER[action.type], PLACEHOLDER[action.type]);
    case "heading":
      return toggleHeading(state, action.level);
    case "bulletList":
      return toggleBulletList(state);
    case "orderedList":
      return toggleOrderedList(state);
    case "quote":
      return toggleQuote(state);
    case "link":
      return insertLink(state, action.url, action.text);
    case "image":
      return insertImage(state, action.url, action.alt);
    case "codeBlock":
      return insertCodeBlock(state, action.language);
    case "table":
      return insertTable(state, action.rows ?? 2, action.cols ?? 2);
    case "horizontalRule":
      return insertBlock(state, "---");
  }
}
function make(text, start, end) {
  const max = text.length;
  const s = Math.max(0, Math.min(start, max));
  const e = Math.max(s, Math.min(end, max));
  return { text, selectionStart: s, selectionEnd: e };
}
function wrapInline(state, marker, placeholder) {
  const { text, selectionStart: start, selectionEnd: end } = state;
  const sel = text.slice(start, end);
  const m = marker.length;
  if (start === end) {
    const next2 = text.slice(0, start) + marker + placeholder + marker + text.slice(end);
    return make(next2, start + m, start + m + placeholder.length);
  }
  if (sel.length >= 2 * m && sel.startsWith(marker) && sel.endsWith(marker)) {
    const inner = sel.slice(m, sel.length - m);
    const next2 = text.slice(0, start) + inner + text.slice(end);
    return make(next2, start, start + inner.length);
  }
  if (text.slice(start - m, start) === marker && text.slice(end, end + m) === marker) {
    const next2 = text.slice(0, start - m) + sel + text.slice(end + m);
    return make(next2, start - m, end - m);
  }
  const next = text.slice(0, start) + marker + sel + marker + text.slice(end);
  return make(next, start + m, end + m);
}
function selectedLines(state) {
  const { text, selectionStart: start, selectionEnd: end } = state;
  const blockStart = text.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
  const searchFrom = end > start && text[end - 1] === "\n" ? end - 1 : end;
  const nl = text.indexOf("\n", searchFrom);
  const blockEnd = nl === -1 ? text.length : nl;
  const lines = text.slice(blockStart, blockEnd).split("\n");
  return { blockStart, blockEnd, lines };
}
function replaceLines(state, block, newLines) {
  const joined = newLines.join("\n");
  const next = state.text.slice(0, block.blockStart) + joined + state.text.slice(block.blockEnd);
  return make(next, block.blockStart, block.blockStart + joined.length);
}
var HEADING_RE = /^(#{1,6})[ \t]+/;
var BULLET_RE = /^[-*+][ \t]+/;
var ORDERED_RE = /^\d+\.[ \t]+/;
var QUOTE_RE = /^>[ \t]?/;
function toggleHeading(state, level) {
  const block = selectedLines(state);
  const prefix = `${"#".repeat(level)} `;
  const allSame = block.lines.every((l) => {
    const m = l.match(HEADING_RE);
    return l.trim() === "" || m?.[1]?.length === level;
  });
  const lines = block.lines.map((l) => {
    if (l.trim() === "") return l;
    const stripped = l.replace(HEADING_RE, "");
    return allSame ? stripped : prefix + stripped;
  });
  return replaceLines(state, block, lines);
}
function toggleBulletList(state) {
  const block = selectedLines(state);
  const allBul = block.lines.every((l) => l.trim() === "" || BULLET_RE.test(l));
  const lines = block.lines.map((l) => {
    if (l.trim() === "") return l;
    return allBul ? l.replace(BULLET_RE, "") : `- ${l.replace(BULLET_RE, "").replace(ORDERED_RE, "")}`;
  });
  return replaceLines(state, block, lines);
}
function toggleOrderedList(state) {
  const block = selectedLines(state);
  const allOrd = block.lines.every((l) => l.trim() === "" || ORDERED_RE.test(l));
  let n = 0;
  const lines = block.lines.map((l) => {
    if (l.trim() === "") return l;
    if (allOrd) return l.replace(ORDERED_RE, "");
    n += 1;
    return `${n}. ${l.replace(ORDERED_RE, "").replace(BULLET_RE, "")}`;
  });
  return replaceLines(state, block, lines);
}
function toggleQuote(state) {
  const block = selectedLines(state);
  const allQuoted = block.lines.every((l) => l.trim() === "" || QUOTE_RE.test(l));
  const lines = block.lines.map((l) => {
    if (allQuoted) return l.replace(QUOTE_RE, "");
    return `> ${l}`;
  });
  return replaceLines(state, block, lines);
}
function insertLink(state, url, text) {
  const { text: src, selectionStart: start, selectionEnd: end } = state;
  const label = text ?? (src.slice(start, end) || "text");
  const href = url ?? "https://";
  const snippet = `[${label}](${href})`;
  const next = src.slice(0, start) + snippet + src.slice(end);
  const hrefStart = start + `[${label}](`.length;
  return make(next, hrefStart, hrefStart + href.length);
}
function insertImage(state, url, alt2) {
  const { text: src, selectionStart: start, selectionEnd: end } = state;
  const altText = alt2 ?? (src.slice(start, end) || "alt");
  const href = url ?? "https://";
  const snippet = `![${altText}](${href})`;
  const next = src.slice(0, start) + snippet + src.slice(end);
  const hrefStart = start + `![${altText}](`.length;
  return make(next, hrefStart, hrefStart + href.length);
}
function insertCodeBlock(state, language) {
  const { text: src, selectionStart: start, selectionEnd: end } = state;
  const body = src.slice(start, end);
  const lang = language ?? "";
  const before = src.slice(0, start);
  const needsNlBefore = before.length > 0 && !before.endsWith("\n");
  const lead = needsNlBefore ? "\n" : "";
  const snippet = `${lead}\`\`\`${lang}
${body}
\`\`\`
`;
  const next = before + snippet + src.slice(end);
  const langAt = before.length + lead.length + 3;
  return make(next, langAt, langAt + lang.length);
}
function insertTable(state, rows, cols) {
  const c = Math.max(1, cols);
  const r = Math.max(1, rows);
  const header = `| ${Array.from({ length: c }, (_, i) => `Column ${i + 1}`).join(" | ")} |`;
  const divider = `| ${Array.from({ length: c }, () => "---").join(" | ")} |`;
  const bodyRows = Array.from(
    { length: r },
    () => `| ${Array.from({ length: c }, () => "   ").join(" | ")} |`
  );
  const table = [header, divider, ...bodyRows].join("\n");
  return insertBlock(state, table);
}
function insertBlock(state, block) {
  const { text: src, selectionStart: start, selectionEnd: end } = state;
  const before = src.slice(0, start);
  const after = src.slice(end);
  const lead = before.length === 0 || before.endsWith("\n") ? "" : "\n";
  const tail = after.length === 0 || after.startsWith("\n") ? "" : "\n";
  const snippet = lead + block + tail;
  const next = before + snippet + after;
  const blockStart = before.length + lead.length;
  return make(next, blockStart, blockStart + block.length);
}

// src/url-policy.ts
var SAFE_URL_SCHEMES = ["http:", "https:", "mailto:"];
var DOCUMENT_URL_SCHEMES = SAFE_URL_SCHEMES;
function documentAllowed(url) {
  const s = scheme(url);
  if (s === null) return false;
  if (s === "") return url.trim().length > 0;
  return DOCUMENT_URL_SCHEMES.includes(`${s}:`) && hasAuthorityOrBody(url, s);
}
function hasAuthorityOrBody(url, s) {
  const u = url.trim();
  if (s === "mailto") return /^mailto:[^\s]+$/i.test(u);
  return /^https?:\/\/./i.test(u);
}
function isSafeDocumentHref(href) {
  return documentAllowed(href) && documentAllowed(decodeProbe(href));
}
var MEDIA_URL_SCHEMES = ["http:", "https:"];
var CONTROL_CHAR = /[\u0000-\u001f\u007f]/;
var NAMED_REFS = {
  colon: ":",
  Tab: "	",
  NewLine: "\n",
  sol: "/",
  num: "#",
  quest: "?",
  amp: "&"
};
var PROBE_LEN = 64;
function decodeProbe(url) {
  let out = "";
  let i = 0;
  while (i < url.length && out.length < PROBE_LEN) {
    if (url[i] !== "&") {
      out += url[i];
      i += 1;
      continue;
    }
    const end = url.indexOf(";", i + 1);
    if (end < 0 || end - i > 12) {
      out += url[i];
      i += 1;
      continue;
    }
    const body = url.slice(i + 1, end);
    let decoded = null;
    if (body.startsWith("#")) {
      const hex = body[1] === "x" || body[1] === "X";
      const n = Number.parseInt(hex ? body.slice(2) : body.slice(1), hex ? 16 : 10);
      if (Number.isFinite(n) && n >= 0 && n <= 1114111) decoded = String.fromCodePoint(n);
    } else {
      decoded = NAMED_REFS[body] ?? null;
    }
    if (decoded == null) {
      out += url[i];
      i += 1;
      continue;
    }
    out += decoded;
    i = end + 1;
  }
  return out;
}
function scheme(url) {
  if (CONTROL_CHAR.test(url)) return null;
  const s = url.trim();
  if (s.length === 0) return "";
  const cut = s.search(/[:/?#]/);
  if (cut < 0 || s[cut] !== ":") return "";
  const name = s.slice(0, cut);
  if (name.length === 0) return null;
  if (!/^[a-zA-Z][a-zA-Z0-9+.-]*$/.test(name)) return null;
  return name.toLowerCase();
}
function mediaAllowed(url) {
  const s = scheme(url);
  if (s === null) return false;
  if (s === "") return true;
  if (MEDIA_URL_SCHEMES.includes(`${s}:`)) return true;
  if (s !== "data") return false;
  const body = url.trim().slice(s.length + 1).toLowerCase();
  return body.startsWith("image/") && !body.startsWith("image/svg");
}
function isSafeImageSrc(src) {
  return mediaAllowed(src) && mediaAllowed(decodeProbe(src));
}

// src/render-markdown.ts
function decodeAttr(escaped) {
  return escaped.replace(/&#39;/g, "'").replace(/&quot;/g, '"').replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");
}
function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
var CODE_SLOT = "\0CODE\0";
function renderInline(raw) {
  const codes = [];
  let text = raw.replace(/`([^`]+)`/g, (_m, code) => {
    codes.push(`<code>${escapeHtml(code)}</code>`);
    return `${CODE_SLOT}${codes.length - 1}${CODE_SLOT}`;
  });
  text = escapeHtml(text);
  text = text.replace(
    /!\[([^\]]*)\]\(([^)\s]+)(?:\s+&quot;([^&]*)&quot;)?\)/g,
    (_m, alt2, url, title) => {
      const src = isSafeImageSrc(decodeAttr(url)) ? ` src="${url}"` : "";
      return `<img${src} alt="${alt2}"${title ? ` title="${title}"` : ""} />`;
    }
  );
  text = text.replace(
    /\[([^\]]+)\]\(([^)\s]+)(?:\s+&quot;([^&]*)&quot;)?\)/g,
    (_m, label, url, title) => {
      if (!isSafeDocumentHref(decodeAttr(url))) {
        return `<a class="docz-blocked-href">${label}</a>`;
      }
      return `<a href="${url}"${title ? ` title="${title}"` : ""}>${label}</a>`;
    }
  );
  text = text.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  text = text.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  text = text.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  text = text.replace(/(^|[^_\w])_([^_\n]+)_/g, "$1<em>$2</em>");
  text = text.replace(/~~([^~]+)~~/g, "<del>$1</del>");
  text = text.replace(/ {2,}\n/g, "<br />\n");
  text = text.replace(new RegExp(`${CODE_SLOT}(\\d+)${CODE_SLOT}`, "g"), (_m, i) => {
    const idx = Number(i);
    return codes[idx] ?? "";
  });
  return text;
}
var HEADING_RE2 = /^(#{1,6})[ \t]+(.*)$/;
var HR_RE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
var BULLET_RE2 = /^[ \t]*[-*+][ \t]+(.*)$/;
var ORDERED_RE2 = /^[ \t]*\d+\.[ \t]+(.*)$/;
var QUOTE_RE2 = /^ {0,3}>[ \t]?(.*)$/;
var TABLE_DIVIDER_RE = /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/;
function tableCells(line) {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|")) s = s.slice(0, -1);
  return s.split("|").map((c) => c.trim());
}
var renderMarkdownToHtml = (markdown) => {
  const lines = markdown.replace(/\r\n?/g, "\n").split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (line.trim() === "") {
      i += 1;
      continue;
    }
    const fence = line.match(/^([ \t]*)(`{3,}|~{3,})(.*)$/);
    if (fence) {
      const marker = fence[2];
      const lang = (fence[3] ?? "").trim();
      const body = [];
      i += 1;
      while (i < lines.length && !(lines[i] ?? "").trim().startsWith(marker)) {
        body.push(lines[i] ?? "");
        i += 1;
      }
      i += 1;
      const cls = lang ? ` class="language-${escapeHtml(lang)}"` : "";
      out.push(`<pre><code${cls}>${escapeHtml(body.join("\n"))}</code></pre>`);
      continue;
    }
    const h = line.match(HEADING_RE2);
    if (h) {
      const level = h[1].length;
      out.push(`<h${level}>${renderInline(h[2].trim())}</h${level}>`);
      i += 1;
      continue;
    }
    if (HR_RE.test(line)) {
      out.push("<hr />");
      i += 1;
      continue;
    }
    if (line.includes("|") && i + 1 < lines.length && TABLE_DIVIDER_RE.test(lines[i + 1] ?? "")) {
      const header = tableCells(line);
      i += 2;
      const rows = [];
      while (i < lines.length && (lines[i] ?? "").includes("|") && (lines[i] ?? "").trim() !== "") {
        rows.push(tableCells(lines[i] ?? ""));
        i += 1;
      }
      out.push(renderTable(header, rows));
      continue;
    }
    if (QUOTE_RE2.test(line)) {
      const quoted = [];
      while (i < lines.length && QUOTE_RE2.test(lines[i] ?? "")) {
        quoted.push(lines[i].match(QUOTE_RE2)?.[1] ?? "");
        i += 1;
      }
      out.push(`<blockquote>
${renderMarkdownToHtml(quoted.join("\n"))}
</blockquote>`);
      continue;
    }
    if (BULLET_RE2.test(line) || ORDERED_RE2.test(line)) {
      const ordered = !BULLET_RE2.test(line) && ORDERED_RE2.test(line);
      const re = ordered ? ORDERED_RE2 : BULLET_RE2;
      const items = [];
      while (i < lines.length && re.test(lines[i] ?? "")) {
        const m = lines[i].match(re);
        items.push(`<li>${renderInline(m?.[1] ?? "")}</li>`);
        i += 1;
      }
      const tag = ordered ? "ol" : "ul";
      out.push(`<${tag}>
${items.join("\n")}
</${tag}>`);
      continue;
    }
    const para = [];
    while (i < lines.length && (lines[i] ?? "").trim() !== "" && !isBlockStart(lines[i] ?? "")) {
      para.push(lines[i] ?? "");
      i += 1;
    }
    out.push(`<p>${renderInline(para.join("\n"))}</p>`);
  }
  return out.join("\n");
};
function isBlockStart(line) {
  return HEADING_RE2.test(line) || HR_RE.test(line) || QUOTE_RE2.test(line) || BULLET_RE2.test(line) || ORDERED_RE2.test(line) || /^([ \t]*)(`{3,}|~{3,})/.test(line);
}
function renderTable(header, rows) {
  const head = header.map((c) => `<th>${renderInline(c)}</th>`).join("");
  const body = rows.map((r) => `<tr>${r.map((c) => `<td>${renderInline(c)}</td>`).join("")}</tr>`).join("\n");
  return `<table>
<thead>
<tr>${head}</tr>
</thead>
<tbody>
${body}
</tbody>
</table>`;
}

// src/controller.ts
function createMarkdownController(options) {
  let value = options.value;
  let onChange = options.onChange;
  let renderMarkdown = options.renderMarkdown ?? renderMarkdownToHtml;
  const commit = (next) => {
    if (next === value) return;
    value = next;
    onChange?.(value);
  };
  return {
    getValue() {
      return value;
    },
    setValue(markdown) {
      commit(markdown);
    },
    applyAction(action, selection) {
      const start = clamp(selection.start, value.length);
      const end = clamp(Math.max(selection.end, start), value.length);
      const result = applyMarkdownAction(
        { text: value, selectionStart: start, selectionEnd: end },
        action
      );
      commit(result.text);
      return result;
    },
    getPreviewHtml() {
      return renderMarkdown(value);
    },
    setOptions(opts) {
      if ("onChange" in opts) onChange = opts.onChange;
      if ("renderMarkdown" in opts) {
        renderMarkdown = opts.renderMarkdown ?? renderMarkdownToHtml;
      }
    }
  };
}
function clamp(n, max) {
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(n, max));
}
var WEB_MARKDOWN_EDITOR_LIB = "@norkron/web-markdown-editor";
var MARKDOWN_EDITOR_LIB_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.web-markdown-editor");
var LIB = WEB_MARKDOWN_EDITOR_LIB;
var WEB_MARKDOWN_EDITOR_TOKEN = MARKDOWN_EDITOR_LIB_TOKEN;
var scoped = createScopedErrors(WEB_MARKDOWN_EDITOR_LIB, MARKDOWN_EDITOR_LIB_TOKEN);
var warn = scoped.warn;
var panic = scoped.panic;
function reportInvalidInput(opts) {
  reportInvalidInput$1({
    lib: WEB_MARKDOWN_EDITOR_LIB,
    libToken: MARKDOWN_EDITOR_LIB_TOKEN,
    fn: opts.fn,
    parseResult: opts.parseResult,
    code: opts.code,
    severity: opts.severity
  });
}

// src/keymap.ts
var mod = (c) => Boolean(c.metaKey || c.ctrlKey);
var shift = (c) => Boolean(c.shiftKey);
var alt = (c) => Boolean(c.altKey);
var key = (c) => c.key.toLowerCase();
var SHORTCUTS = [
  {
    hint: "\u2318B",
    action: { type: "bold" },
    match: (c) => mod(c) && !alt(c) && !shift(c) && key(c) === "b"
  },
  {
    hint: "\u2318I",
    action: { type: "italic" },
    match: (c) => mod(c) && !alt(c) && !shift(c) && key(c) === "i"
  },
  {
    hint: "\u2318E",
    action: { type: "inlineCode" },
    match: (c) => mod(c) && !alt(c) && !shift(c) && key(c) === "e"
  },
  {
    hint: "\u2318K",
    action: { type: "link" },
    match: (c) => mod(c) && !alt(c) && !shift(c) && key(c) === "k"
  },
  {
    hint: "\u2318\u21E7X",
    action: { type: "strikethrough" },
    match: (c) => mod(c) && shift(c) && key(c) === "x"
  },
  {
    hint: "\u2318\u21E77",
    action: { type: "orderedList" },
    match: (c) => mod(c) && shift(c) && (key(c) === "7" || key(c) === "&")
  },
  {
    hint: "\u2318\u21E78",
    action: { type: "bulletList" },
    match: (c) => mod(c) && shift(c) && (key(c) === "8" || key(c) === "*")
  },
  {
    hint: "\u2318\u21E7.",
    action: { type: "quote" },
    match: (c) => mod(c) && shift(c) && (key(c) === "." || key(c) === ">")
  },
  {
    hint: "\u2318\u21E7C",
    action: { type: "codeBlock" },
    match: (c) => mod(c) && shift(c) && key(c) === "c"
  },
  // Headings: ⌘⌥1 … ⌘⌥6.
  ...[1, 2, 3, 4, 5, 6].map((level) => ({
    hint: `\u2318\u2325${level}`,
    action: { type: "heading", level },
    match: (c) => mod(c) && alt(c) && key(c) === String(level)
  }))
];
function matchShortcut(chord, shortcuts = SHORTCUTS) {
  for (const s of shortcuts) {
    if (s.match(chord)) return s.action;
  }
  return null;
}

// src/toolbar.ts
var DEFAULT_TOOLBAR = [
  { id: "bold", label: "Bold", glyph: "B", action: { type: "bold" }, shortcutHint: "\u2318B" },
  { id: "italic", label: "Italic", glyph: "I", action: { type: "italic" }, shortcutHint: "\u2318I" },
  {
    id: "strikethrough",
    label: "Strikethrough",
    glyph: "S",
    action: { type: "strikethrough" },
    shortcutHint: "\u2318\u21E7X"
  },
  {
    id: "heading",
    label: "Heading",
    glyph: "H2",
    action: { type: "heading", level: 2 },
    shortcutHint: "\u2318\u23252"
  },
  {
    id: "bulletList",
    label: "Bullet list",
    glyph: "\u2022",
    action: { type: "bulletList" },
    shortcutHint: "\u2318\u21E78"
  },
  {
    id: "orderedList",
    label: "Numbered list",
    glyph: "1.",
    action: { type: "orderedList" },
    shortcutHint: "\u2318\u21E77"
  },
  { id: "link", label: "Link", glyph: "\u{1F517}", action: { type: "link" }, shortcutHint: "\u2318K" },
  {
    id: "inlineCode",
    label: "Inline code",
    glyph: "\u2039\u203A",
    action: { type: "inlineCode" },
    shortcutHint: "\u2318E"
  },
  {
    id: "codeBlock",
    label: "Code block",
    glyph: "{ }",
    action: { type: "codeBlock" },
    shortcutHint: "\u2318\u21E7C"
  },
  {
    id: "quote",
    label: "Quote",
    glyph: "\u275D",
    action: { type: "quote" },
    shortcutHint: "\u2318\u21E7."
  },
  { id: "table", label: "Table", glyph: "\u25A6", action: { type: "table", rows: 2, cols: 2 } }
];
function useMarkdownEditor(options) {
  const { value, onChange, renderMarkdown, shortcuts, readOnly } = options;
  const textareaRef = useRef(null);
  const pendingSelection = useRef(null);
  const render = renderMarkdown ?? renderMarkdownToHtml;
  const previewHtml = useMemo(() => render(value), [render, value]);
  useLayoutEffect(() => {
    const el = textareaRef.current;
    const sel = pendingSelection.current;
    if (el && sel) {
      el.focus();
      el.setSelectionRange(sel.start, sel.end);
      pendingSelection.current = null;
    }
  }, [value]);
  const runAction = useCallback(
    (action) => {
      if (readOnly) return;
      const el = textareaRef.current;
      const start = el ? el.selectionStart : value.length;
      const end = el ? el.selectionEnd : value.length;
      const next = applyMarkdownAction(
        { text: value, selectionStart: start, selectionEnd: end },
        action
      );
      pendingSelection.current = { start: next.selectionStart, end: next.selectionEnd };
      onChange(next.text);
    },
    [onChange, readOnly, value]
  );
  const onKeyDown = useCallback(
    (event) => {
      if (readOnly) return;
      const chord = {
        key: event.key,
        metaKey: event.metaKey,
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey
      };
      const action = matchShortcut(chord, shortcuts);
      if (action) {
        event.preventDefault();
        runAction(action);
      }
    },
    [readOnly, runAction, shortcuts]
  );
  const onInput = useCallback(
    (event) => {
      if (readOnly) return;
      onChange(event.target.value);
    },
    [onChange, readOnly]
  );
  return { textareaRef, previewHtml, runAction, onKeyDown, onInput };
}
var ROOT_CLASS = "nk-md-editor";
function MarkdownEditor(props) {
  const {
    value,
    onChange,
    preview = true,
    layout = "split",
    toolbar = true,
    renderMarkdown,
    toolbarItems = DEFAULT_TOOLBAR,
    placeholder = "Write markdown\u2026",
    readOnly = false,
    className,
    testId = "markdown-editor",
    ariaLabel = "Markdown source"
  } = props;
  const [showPreview, setShowPreview] = useState(preview);
  const { textareaRef, previewHtml, runAction, onKeyDown, onInput } = useMarkdownEditor({
    value,
    onChange,
    renderMarkdown,
    readOnly
  });
  const previewOn = showPreview && layout !== "source-only";
  const tabbed = layout === "tabs";
  const sourceVisible = !tabbed || !previewOn;
  const previewVisible = previewOn && (!tabbed || showPreview);
  const rootClass = [
    ROOT_CLASS,
    `${ROOT_CLASS}--${layout}`,
    previewOn ? `${ROOT_CLASS}--preview` : `${ROOT_CLASS}--no-preview`,
    className
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs("div", { className: rootClass, "data-testid": testId, "data-norkron-lib": "@norkron/web-markdown-editor", children: [
    toolbar && /* @__PURE__ */ jsxs(
      "div",
      {
        className: `${ROOT_CLASS}__toolbar`,
        role: "toolbar",
        "aria-label": "Formatting",
        "data-testid": "md-toolbar",
        children: [
          toolbarItems.map((item) => /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: `${ROOT_CLASS}__tool`,
              "data-testid": `md-toolbar-${item.id}`,
              title: item.shortcutHint ? `${item.label} (${item.shortcutHint})` : item.label,
              "aria-label": item.label,
              disabled: readOnly,
              onMouseDown: (e) => e.preventDefault(),
              onClick: () => runAction(item.action),
              children: item.glyph
            },
            item.id
          )),
          layout !== "source-only" && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: `${ROOT_CLASS}__toggle`,
              "data-testid": "md-preview-toggle",
              "aria-pressed": showPreview,
              title: showPreview ? "Hide preview" : "Show preview",
              onClick: () => setShowPreview((v) => !v),
              children: showPreview ? "Hide preview" : "Show preview"
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: `${ROOT_CLASS}__panes`, children: [
      sourceVisible && /* @__PURE__ */ jsx(
        "textarea",
        {
          ref: textareaRef,
          className: `${ROOT_CLASS}__source`,
          "data-testid": "md-source",
          value,
          placeholder,
          readOnly,
          spellCheck: true,
          "aria-label": ariaLabel,
          onChange: onInput,
          onKeyDown
        }
      ),
      previewVisible && /* @__PURE__ */ jsx(
        "section",
        {
          className: `${ROOT_CLASS}__preview`,
          "data-testid": "md-preview",
          "aria-label": "Preview",
          dangerouslySetInnerHTML: { __html: previewHtml }
        }
      )
    ] })
  ] });
}

// src/version.ts
var LIB_VERSION = "0.1.1";

export { DEFAULT_TOOLBAR, LIB, LIB_VERSION, MarkdownEditor, SHORTCUTS, WEB_MARKDOWN_EDITOR_LIB, WEB_MARKDOWN_EDITOR_TOKEN, applyMarkdownAction, createMarkdownController, escapeHtml, insertBlock, insertCodeBlock, insertImage, insertLink, insertTable, matchShortcut, panic, renderInline, renderMarkdownToHtml, reportInvalidInput, toggleBulletList, toggleHeading, toggleOrderedList, toggleQuote, useMarkdownEditor, warn, wrapInline };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map